import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Timer } from "@phosphor-icons/react";
import api from "../lib/api";

// ─── Idle-timeout configuration ────────────────────────────────────────────────
// The idle window is CONFIGURED PER-ROLE and pushed to the client via
// `/auth/me` (`user.idle_timeout_minutes`). Backend JWTs are issued with an
// expiry that matches — so a stale token can never outlive the idle logoff.
const FALLBACK_IDLE_MINUTES = 30;
const WARN_LEAD_MS  =  2 * 60 * 1000; // last 2 minutes before logout
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "click"];
const ACTIVITY_THROTTLE_MS = 5_000;
// Wall-clock tick — the interval that actually decides when to warn or log
// out. Using a single interval (instead of separate setTimeouts) makes the
// component immune to two subtle bugs: (1) `showWarn` state changes cancelling
// the pending logout setTimeout on effect re-run, and (2) browser background-
// tab throttling delaying long setTimeouts past their intended fire time.
const TICK_MS = 1_000;

/**
 * Auto-logs the current user (voter or admin) out after N minutes of no
 * activity, with a 2-minute warning dialog they can dismiss to stay signed in.
 * Mounts globally but is a no-op until a session is authenticated.
 */
export default function IdleTimeout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const isAuthed = !!(user && user.role);
    const idleMinutes = Number(user?.idle_timeout_minutes) > 0 ? Number(user.idle_timeout_minutes) : FALLBACK_IDLE_MINUTES;
    const IDLE_LIMIT_MS = idleMinutes * 60 * 1000;

    const [showWarn, setShowWarn] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(Math.ceil(WARN_LEAD_MS / 1000));
    const lastActivityRef = useRef(Date.now());
    const showWarnRef = useRef(false);
    const loggingOutRef = useRef(false);

    // Track user activity — throttle to a coarse 5-second granularity so we
    // never thrash setState just from a moving mouse. We do NOT reset while
    // the warning is on-screen: the user must explicitly click "Stay signed
    // in" so an idle mouse can't silently keep an abandoned session alive.
    useEffect(() => {
        if (!isAuthed) return undefined;
        const onActivity = () => {
            if (showWarnRef.current) return;
            const now = Date.now();
            if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
            lastActivityRef.current = now;
        };
        ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
        return () => ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
    }, [isAuthed]);

    // The single decision-making loop. Every second we look at wall-clock
    // idle time and decide whether to warn or log out. Because everything is
    // computed against `Date.now()` (not scheduled `setTimeout`s), it's
    // completely immune to state-driven effect restarts and browser
    // background-tab timer throttling.
    useEffect(() => {
        if (!isAuthed) {
            setShowWarn(false);
            showWarnRef.current = false;
            loggingOutRef.current = false;
            lastActivityRef.current = Date.now();
            return undefined;
        }

        lastActivityRef.current = Date.now();

        const tick = () => {
            if (loggingOutRef.current) return;
            const idleMs = Date.now() - lastActivityRef.current;
            const remainingMs = IDLE_LIMIT_MS - idleMs;

            if (remainingMs <= 0) {
                // Auto-logout: mark first to avoid duplicate fires while the
                // async logout is in flight, then navigate as a hard fallback
                // in case ProtectedRoute doesn't re-render fast enough.
                loggingOutRef.current = true;
                setShowWarn(false);
                showWarnRef.current = false;
                toast.info(`You were signed out after ${idleMinutes} minutes of inactivity.`);
                (async () => {
                    try { await logout(); } catch { /* client-side fallback */ }
                    try { navigate(user?.role === "admin" ? "/login/admin" : "/login/voter", { replace: true }); } catch { /* noop */ }
                })();
                return;
            }

            const inWarningWindow = remainingMs <= WARN_LEAD_MS;
            setSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
            if (inWarningWindow && !showWarnRef.current) {
                showWarnRef.current = true;
                setShowWarn(true);
            }
        };

        tick(); // fire once immediately in case a resume already put us past the limit
        const interval = setInterval(tick, TICK_MS);
        return () => clearInterval(interval);
    }, [isAuthed, IDLE_LIMIT_MS, idleMinutes, logout, navigate, user?.role]);

    const stayIn = async () => {
        setShowWarn(false);
        showWarnRef.current = false;
        lastActivityRef.current = Date.now();
        // Refresh the backend JWT so its expiry matches the fresh idle window
        // — otherwise a token issued at login could quietly expire mid-session.
        try { await api.post("/auth/refresh"); } catch { /* silent — the client-side timer still resets */ }
    };

    const signOutNow = async () => {
        loggingOutRef.current = true;
        setShowWarn(false);
        showWarnRef.current = false;
        try { await logout(); } catch { /* noop */ }
        try { navigate(user?.role === "admin" ? "/login/admin" : "/login/voter", { replace: true }); } catch { /* noop */ }
    };

    if (!isAuthed) return null;

    const mm = String(Math.floor(secondsLeft / 60)).padStart(1, "0");
    const ss = String(secondsLeft % 60).padStart(2, "0");

    return (
        <AlertDialog open={showWarn}>
            <AlertDialogContent data-testid="idle-timeout-warning">
                <AlertDialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="h-10 w-10 grid place-items-center rounded-md bg-amber-50 border border-amber-200 shrink-0">
                            <Timer size={20} weight="duotone" className="text-amber-600" />
                        </div>
                        <div>
                            <AlertDialogTitle className="font-heading text-xl">Still there?</AlertDialogTitle>
                            <AlertDialogDescription>
                                For your security, you'll be signed out automatically after {idleMinutes} minutes of inactivity.
                            </AlertDialogDescription>
                        </div>
                    </div>
                </AlertDialogHeader>
                <div className="mt-2 text-center">
                    <div className="overline text-slate-500">Signing you out in</div>
                    <div className="hash-mono text-4xl font-bold text-slate-900 tabular-nums mt-1" data-testid="idle-timeout-countdown">
                        {mm}:{ss}
                    </div>
                </div>
                <AlertDialogFooter className="gap-2">
                    <Button variant="outline" onClick={signOutNow} data-testid="idle-timeout-signout">
                        Sign out now
                    </Button>
                    <Button
                        onClick={stayIn}
                        className="bg-civic-600 hover:bg-civic-700 text-white"
                        data-testid="idle-timeout-stay"
                    >
                        Stay signed in
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
